/**
 * App-Shell — Rail + Sub-Sidebar + Topbar + Main-Container (Phase 3.5 §5).
 *
 * 1:1 zu `cotrainer-apps-script/app/Index.html`. Shell wird einmal gemountet
 * (von main.ts nach probeAuth), bleibt persistent zwischen Routenwechseln.
 *
 * K1-Update (2026-05-24): Alle 9 Apps-Script-Bereiche werden in der Rail
 * gerendert. Coming-Soon-Bereiche bekommen Lock-Overlay + disabled-State.
 * K2-Update (2026-05-24): Breadcrumb für Hub-Bereich zeigt nur "Hub",
 * für andere Bereiche "Bereich › View".
 */

import { logoutBackend } from "../api/client";
import type { AppBootstrapResult, HubContext, UserContext } from "../api/types";
import {
  type BereichDef,
  type GroupDef,
  type ViewDef,
  allBereicheForRail,
  defaultRouteForGroup,
  findGroupForView,
  findViewForRoute,
  flattenedViews,
  isBereichLiveForUser,
  visibleGroupsForUser,
  visibleViewsForUser
} from "../lib/bereiche";
import { icon } from "../lib/icons";
import { avatarHtml, hydrateAvatars } from "./avatar";
import { mountTopbarStempel } from "../views/_topbar-stempel";

const SHELL_ID = "app-shell";
const VIEW_CONTAINER_ID = "view-container";

let currentUser: UserContext | null = null;
let subsidebarCollapsed = false;
/**
 * Sub-Iter 2: Bootstrap-Response wird vom Hub-View nach erfolgreicher Last
 * via setBootstrap() global gecached. Topbar liest daraus die Pills (kein
 * 2. /app/bootstrap-Call). Wenn null → Topbar zeigt keine Pills.
 */
let cachedBootstrap: AppBootstrapResult | null = null;

export function setBootstrap(b: AppBootstrapResult | null): void {
  cachedBootstrap = b;
  // Topbar neu rendern wenn schon gemounted (Pills füllen sich nach Hub-Load)
  const tb = document.getElementById("topbar");
  if (tb && currentUser) {
    tb.innerHTML = topbarHtml(currentUser);
    attachTopbarHandlers();
  }
}

export function getBootstrap(): AppBootstrapResult | null {
  return cachedBootstrap;
}

// =============================================================================
// Mount + Update
// =============================================================================

export function mountShell(host: HTMLElement, user: UserContext): void {
  currentUser = user;
  host.innerHTML = shellHtml(user);
  attachShellHandlers();
  void hydrateAvatars(host); // Rail-Avatar async nachladen (DSGVO: fetch+Blob)
}

export function isShellMounted(): boolean {
  return document.getElementById(SHELL_ID) !== null;
}

export function getViewContainer(): HTMLElement {
  const el = document.getElementById(VIEW_CONTAINER_ID);
  if (!el) {
    throw new Error("[app-shell] view-container not mounted — call mountShell() first");
  }
  return el;
}

export function setActiveRoute(path: string): void {
  if (!currentUser) return;
  const match = findViewForRoute(path);
  const activeBereichSlug = match?.bereich.slug ?? null;
  const activeViewKey = match?.view.key ?? null;

  document.querySelectorAll<HTMLElement>("[data-rail-bereich]").forEach((el) => {
    const slug = el.dataset.railBereich;
    el.classList.toggle("is-current", slug === activeBereichSlug);
  });

  const sub = document.getElementById("subsidebar");
  const expandBtn = document.getElementById("subsidebar-expand-btn");
  if (sub) {
    const bereich = match?.bereich ?? null;
    // Hub-Bereich: Sub-Sidebar komplett verstecken (1:1 zu Apps-Script
    // Index.html Z. 985-991). Hub-View nutzt volle Breite.
    const hideSubsidebar = !bereich || bereich.slug === "hub";
    sub.classList.toggle("is-hidden", hideSubsidebar);
    if (hideSubsidebar) {
      sub.innerHTML = "";
      sub.classList.remove("is-collapsed");
      expandBtn?.classList.remove("is-visible");
    } else {
      sub.innerHTML = subsidebarHtml(currentUser, bereich, activeViewKey);
      attachSubsidebarHandlers();
      void hydrateAvatars(sub); // Footer-Avatar async nachladen
    }
  }
}

/**
 * Re-rendert Rail- + Footer-Avatar aus dem (mutierten) currentUser — von der
 * /profil-View nach Upload/Delete aufgerufen, damit das Avatar sofort ohne
 * Reload aktualisiert. currentUser ist dieselbe Objekt-Referenz wie der
 * Router-User; die View mutiert profilbild_key, hier folgt nur der DOM-Refresh.
 */
export function refreshUserChrome(): void {
  if (!currentUser) return;
  const rail = document.querySelector(".rail-user");
  if (rail) {
    const tmp = document.createElement("div");
    tmp.innerHTML = railUserHtml(currentUser).trim();
    const fresh = tmp.firstElementChild;
    if (fresh) rail.replaceWith(fresh);
  }
  const footAvatar = document.querySelector(".subsidebar-foot .ct-avatar");
  if (footAvatar) {
    const tmp = document.createElement("div");
    tmp.innerHTML = avatarHtml({
      vollname: currentUser.vollname,
      email: currentUser.email,
      farbe: currentUser.farbe,
      profilbildKey: currentUser.profilbild_key,
      personId: currentUser.person_id,
      size: 36
    }).trim();
    const fresh = tmp.firstElementChild;
    if (fresh) footAvatar.replaceWith(fresh);
  }
  void hydrateAvatars(document); // neue Token-Platzhalter (Rail + Footer) nachladen
}

// =============================================================================
// Shell HTML
// =============================================================================

function shellHtml(user: UserContext): string {
  return `
    <div id="${SHELL_ID}" class="app-shell">
      <aside class="rail" aria-label="Bereiche">
        <a href="/" data-router-link class="rail-logo has-image" data-rail-bereich="hub" title="Hub">
          <img src="/logo.png" alt="CoTrainer" />
        </a>
        <div class="rail-divider"></div>
        <nav class="rail-nav">
          ${railItemsHtml(user)}
        </nav>
        <div class="rail-divider-foot"></div>
        ${railUserHtml(user)}
      </aside>

      <aside id="subsidebar" class="subsidebar is-hidden">
      </aside>

      <button id="subsidebar-expand-btn" class="subsidebar-expand-btn${subsidebarCollapsed ? " is-visible" : ""}" type="button" aria-label="Sidebar einblenden">
        ${icon("chevron-right", 14)}
      </button>

      <main class="main">
        <div class="topbar" id="topbar">
          ${topbarHtml(user)}
        </div>
        <div class="view-wrap">
          <div id="${VIEW_CONTAINER_ID}" class="view-container"></div>
        </div>
      </main>
    </div>
  `;
}

// =============================================================================
// Rail — alle 9 Apps-Script-Bereiche, Live + Coming-Soon
// =============================================================================

function railItemsHtml(user: UserContext): string {
  // Hub ist schon als Logo oben — alle anderen Bereiche werden hier gerendert
  return allBereicheForRail()
    .filter((b) => b.slug !== "hub")
    .map((b) => railItemHtml(b, user))
    .join("");
}

function railItemHtml(b: BereichDef, user: UserContext): string {
  const isLive = isBereichLiveForUser(b, user);
  const classes = ["rail-item"];
  if (!isLive) classes.push("is-coming-soon");

  const tooltip = isLive ? b.label : `${b.label} (kommt bald)`;
  const lockOverlay = !isLive
    ? `<span class="rail-lock-overlay">${icon("lock", 10)}</span>`
    : "";

  // Coming-Soon-Bereiche bekommen kein href + kein data-router-link → nicht klickbar
  if (!isLive) {
    return `
      <div
        data-rail-bereich="${escapeAttr(b.slug)}"
        class="${classes.join(" ")}"
        title="${escapeAttr(tooltip)}"
        aria-disabled="true"
      >
        ${icon(b.iconKey, 22)}
        ${lockOverlay}
        <span class="rail-tooltip">${escapeHtml(tooltip)}</span>
      </div>
    `;
  }

  return `
    <a href="${defaultRouteForBereich(b)}" data-router-link
       data-rail-bereich="${escapeAttr(b.slug)}"
       class="${classes.join(" ")}"
       title="${escapeAttr(tooltip)}">
      ${icon(b.iconKey, 22)}
      <span class="rail-tooltip">${escapeHtml(tooltip)}</span>
    </a>
  `;
}

function defaultRouteForBereich(b: BereichDef): string {
  const flat = flattenedViews(b);
  const firstLive = flat.find((v) => !v.comingSoon);
  if (firstLive) return firstLive.route;
  const firstAny = flat[0];
  return firstAny ? firstAny.route : "/";
}

// =============================================================================
// Sub-Sidebar
// =============================================================================

function subsidebarHtml(user: UserContext, bereich: BereichDef | null, activeViewKey: string | null): string {
  // Hub-Fall wird in setActiveRoute() bereits als is-hidden behandelt — diese
  // Funktion wird dann nicht aufgerufen. Falls doch (z.B. initial-Render),
  // empty string returnen damit nichts gerendert wird.
  if (!bereich || bereich.slug === "hub") return "";

  if (bereich.comingSoon) {
    // §5f: subsidebar-head ist jetzt 56px fixed → "Kommt..."-Hinweis raus aus
    // Head (Höhe bleibt bündig mit Topbar). Hinweis-Text in nav-Sektion.
    return `
      <button class="subsidebar-collapse-btn" type="button" aria-label="Sidebar einklappen">
        ${icon("chevron-left", 14)}
      </button>
      <div class="subsidebar-head">
        <div class="subsidebar-head-row">
          <span class="subsidebar-badge">${icon(bereich.iconKey, 14)}</span>
          <span class="subsidebar-title">${escapeHtml(bereich.label)}</span>
        </div>
      </div>
      <div class="subsidebar-nav">
        <div style="padding:14px 16px;font-size:13px;color:var(--paper-mute);font-style:italic;">Kommt in einer späteren Phase</div>
      </div>
      ${subsidebarFooterHtml(user)}
    `;
  }

  // 2-stufige Struktur (Vertrieb): groups + Sub-Views
  // Flat-Struktur (alle anderen): direkt views
  const navHtml = bereich.groups && bereich.groups.length > 0
    ? groupedNavHtml(bereich, user, activeViewKey)
    : flatNavHtml(bereich, user, activeViewKey);

  return `
    <button class="subsidebar-collapse-btn" type="button" aria-label="Sidebar einklappen">
      ${icon("chevron-left", 14)}
    </button>
    <div class="subsidebar-head">
      <div class="subsidebar-head-row">
        <span class="subsidebar-badge">${icon(bereich.iconKey, 14)}</span>
        <span class="subsidebar-title">${escapeHtml(bereich.label)}</span>
      </div>
    </div>
    <nav class="subsidebar-nav">
      ${navHtml}
    </nav>
    ${subsidebarFooterHtml(user)}
  `;
}

function flatNavHtml(bereich: BereichDef, user: UserContext, activeViewKey: string | null): string {
  const views = visibleViewsForUser(bereich, user);
  if (views.length === 0) {
    return `<div class="subsidebar-empty">Noch keine Views verfügbar</div>`;
  }
  return views.map((v) => subItemHtml(v, activeViewKey)).join("");
}

function groupedNavHtml(bereich: BereichDef, user: UserContext, activeViewKey: string | null): string {
  // isSettings-Gruppen werden wieder als regulärer Single-Link gerendert
  // (Eintrag "Einstellungen" → /vertrieb/einstellungen). Erreicht über
  // hideAsSubTab=true an allen Sub-Views + defaultRoute auf die Container-Route.
  const groups = visibleGroupsForUser(bereich, user);
  if (groups.length === 0) {
    return `<div class="subsidebar-empty">Noch keine Bereiche verfügbar</div>`;
  }
  const activeGroup = findGroupForView(bereich, activeViewKey);
  return groups.map((g) => groupHtml(g, activeGroup?.key === g.key, activeViewKey)).join("");
}

function groupHtml(g: GroupDef, isActiveGroup: boolean, activeViewKey: string | null): string {
  // Standalone: genau 1 sichtbare View, kein hideAsSubTab → Single-Link mit
  // Group-Icon (kein Header + Sub-Tab-Block).
  const displaySubViews = g.views.filter((v) => !v.hideAsSubTab);
  if (g.views.length === 1 && displaySubViews.length === 1) {
    const v = displaySubViews[0]!;
    const isCurrent = v.key === activeViewKey;
    const classes = ["sub-group-link"];
    if (isCurrent) classes.push("is-current");
    if (v.comingSoon) classes.push("is-coming-soon");
    return `
      <a href="${escapeAttr(v.route)}" data-router-link class="${classes.join(" ")}">
        <span class="sub-group-icon">${icon(g.iconKey, 14)}</span>
        <span class="sub-group-label">${escapeHtml(g.label)}</span>
        ${v.comingSoon ? `<span class="sub-group-lock">${icon("lock", 11)}</span>` : ""}
      </a>
    `;
  }

  // Multi-View: Group-Header (klickbar = Default-Route, meist hideAsSubTab-
  // Overview-View) + sichtbare Sub-Tab-Liste darunter.
  const headerRoute = defaultRouteForGroup(g);
  // Header gilt als "current" wenn aktive View die hideAsSubTab-Overview ist.
  const headerView = g.views.find((v) => v.hideAsSubTab);
  const isHeaderCurrent = headerView != null && headerView.key === activeViewKey;

  const headerClasses = ["sub-group-header"];
  if (isHeaderCurrent) headerClasses.push("is-current");
  else if (isActiveGroup) headerClasses.push("is-active-group");

  const subTabsHtml = displaySubViews
    .map((v) => subItemHtml(v, activeViewKey))
    .join("");

  return `
    <div class="sub-group">
      <a href="${escapeAttr(headerRoute)}" data-router-link class="${headerClasses.join(" ")}">
        <span class="sub-group-icon">${icon(g.iconKey, 14)}</span>
        <span class="sub-group-label">${escapeHtml(g.label)}</span>
      </a>
      ${subTabsHtml ? `<div class="sub-group-views">${subTabsHtml}</div>` : ""}
    </div>
  `;
}

function subItemHtml(v: ViewDef, activeViewKey: string | null): string {
  const classes = ["sub-item"];
  if (v.key === activeViewKey) classes.push("is-current");
  if (v.comingSoon) classes.push("is-coming-soon");
  const lock = v.comingSoon ? `${icon("lock", 12)} ` : "";
  return `
    <a href="${escapeAttr(v.route)}" data-router-link class="${classes.join(" ")}">
      ${lock}${escapeHtml(v.label)}
    </a>
  `;
}

/** Rail-Avatar unten in der Rail — immer sichtbar, klickbar → /profil. */
function railUserHtml(user: UserContext): string {
  return `
    <a href="/profil" data-router-link class="rail-user" title="${escapeAttr(user.vollname || user.email)} — Profil">
      ${avatarHtml({
        vollname: user.vollname,
        email: user.email,
        farbe: user.farbe,
        profilbildKey: user.profilbild_key,
        personId: user.person_id,
        size: 36
      })}
    </a>
  `;
}

function subsidebarFooterHtml(user: UserContext): string {
  const adminBadge = user.isAdmin
    ? `<span class="subsidebar-foot-admin">Admin</span>`
    : "";
  return `
    <div class="subsidebar-foot">
      <a class="subsidebar-foot-row subsidebar-foot-link" href="/profil" data-router-link title="Profil öffnen">
        <div class="subsidebar-foot-info">
          <div class="subsidebar-foot-user">${escapeHtml(user.vollname || user.email)}</div>
          <div class="subsidebar-foot-email">${escapeHtml(user.email)} · ${escapeHtml(user.person_id || "?")}</div>
        </div>
        ${adminBadge}
      </a>
    </div>
  `;
}

// =============================================================================
// Topbar — Sub-Iter 2: Pills 1:1 zu app/Index.html AppTopbar
// =============================================================================

function topbarHtml(user: UserContext): string {
  const hub = cachedBootstrap?.hub_context ?? null;
  return `
    <div class="tb-spacer"></div>
    ${renderBereichsziel(hub)}
    ${renderCallerSollPills(user, hub)}
    ${renderReminderPills(user, hub)}
    <div class="tb-user-area">
      <div id="topbar-stempel-slot" class="tb-stempel-slot"></div>
      ${user._fromCache ? `<span class="header-badge header-badge-cache" title="UserContext aus KV-Cache">cache</span>` : ""}
      <button id="header-logout-btn" type="button" class="app-logout-btn" aria-label="Logout" title="Logout">${icon("log-out", 18)}</button>
    </div>
  `;
}

// =============================================================================
// Bereichsziel-Block (1:1 zu app/Index.html Z. 508-532)
// =============================================================================

function renderBereichsziel(hub: HubContext | null): string {
  const bz = hub?.bereichsziel;
  if (!bz || !bz.gesetzt) return "";

  const fortschritt = bz.fortschritt_pct ?? 0;
  const istYear = bz.ist_year ?? 0;
  const wert = bz.wert ?? 0;
  const fillCls = bereichszielFillClass(bz.status);

  const jahr = new Date().getFullYear();
  const label = `Closings ${jahr}`;
  const valStr = `${istYear}/${wert}`;
  const pctStr = `${fortschritt}%`;

  return `
    <a class="tb-bz" href="/ziele" data-router-link
       title="Vertriebs-Bereichsziel — klick führt zur Ziele-View">
      <div class="tb-bz-row">
        <span class="tb-bz-label">${escapeHtml(label)}</span>
        <span class="tb-bz-val">${escapeHtml(valStr)}</span>
        <span class="tb-bz-pct">${escapeHtml(pctStr)}</span>
      </div>
      <div class="tb-bz-bar">
        <div class="tb-bz-fill ${fillCls}" style="width:${fortschritt}%"></div>
      </div>
    </a>
  `;
}

function bereichszielFillClass(status?: string): string {
  if (status === "verzug") return "warning";
  if (status === "kritisch" || status === "stichtag_ueberschritten") return "danger";
  return "";
}

// =============================================================================
// Soll-Pills (PG/MT für Caller — 1:1 zu app/Index.html Z. 535-571)
// =============================================================================

function renderCallerSollPills(user: UserContext, hub: HubContext | null): string {
  if (!user.aktiveFunktionen.includes("Caller")) return "";

  const k = hub?.caller_kpis;
  if (!k) {
    return (
      `<span class="tb-soll-pill" title="Caller-Werte diese Woche"><span class="lbl">PG</span><span>—</span></span>` +
      `<span class="tb-soll-pill" title="Caller-Werte diese Woche"><span class="lbl">MT</span><span>—</span></span>`
    );
  }

  const clsPg = classifyErfuellung(k.pg, k.soll_pg);
  const clsMt = classifyErfuellung(k.mt, k.soll_mt);

  return sollPillHtml("PG", k.pg, k.soll_pg, clsPg) + sollPillHtml("MT", k.mt, k.soll_mt, clsMt);
}

function sollPillHtml(label: string, ist: number, soll: number, cls: string): string {
  const valStr = soll && soll > 0 ? `${ist}/${soll}` : String(ist);
  return `
    <span class="tb-soll-pill ${cls}" title="${escapeAttr(label)} diese Woche">
      <span class="lbl">${escapeHtml(label)}</span>
      <span>${escapeHtml(valStr)}</span>
    </span>
  `;
}

function classifyErfuellung(ist: number, soll: number): string {
  if (!soll || soll === 0) return "";
  const erf = ist / soll;
  if (erf >= 1.0) return "ok";
  if (erf >= 0.7) return "warn";
  return "crit";
}

// =============================================================================
// Reminder-Pills (Pflegelücken + HubSpot — 1:1 zu app/Index.html Z. 574-619)
// =============================================================================

function renderReminderPills(user: UserContext, hub: HubContext | null): string {
  const hatVertriebsBezug =
    user.aktiveFunktionen.includes("Caller") ||
    user.aktiveFunktionen.includes("Host_MT") ||
    user.aktiveFunktionen.includes("Host_FU");
  const isAdmin = !!user.isAdmin;
  const isBL = user.berechtigungen.some(
    (b) => b.bereich.toLowerCase() === "vertrieb" && b.rolle.toLowerCase() === "bereichsleiter"
  );
  if (!hatVertriebsBezug && !isAdmin && !isBL) return "";

  // null = Bootstrap-Teilabfrage fehlgeschlagen/unbekannt (NICHT 0). Echte 0 = {count:0}.
  // Verhindert, dass ein Read-Fehler unter Sheets-Last als "0" erscheint (Justin 2026-05-30).
  const pflegCount = hub?.pfleg_pill ? hub.pfleg_pill.count : null;
  const hsCount = hub?.hubspot_tasks ? hub.hubspot_tasks.count : null;

  return reminderPillHtml("Pflegelücken", pflegCount, "/pflegeluecken") +
         reminderPillHtml("HubSpot", hsCount, null);
}

function reminderPillHtml(label: string, count: number | null, navTo: string | null): string {
  const unknown = count === null;
  const display = unknown ? "—" : String(count);
  const cls = unknown ? "count-unknown" : severityClassByCount(count);
  const canNav = !!navTo && !unknown && count > 0;
  const navAttrs = canNav ? ` href="${escapeAttr(navTo)}" data-router-link` : "";
  const tag = canNav ? "a" : "span";
  const title = unknown
    ? `${label} — aktuell nicht verfügbar (lädt)`
    : `${label} (own-only)`;
  return `
    <${tag} class="tb-rem-pill ${cls}"${navAttrs} title="${escapeAttr(title)}">
      <span>${escapeHtml(label)}</span>
      <span class="count">${escapeHtml(display)}</span>
    </${tag}>
  `;
}

// Severity-Mapping nach Count: 0=grau (count-zero), 1-5=gelb (severity-low),
// 6-10=orange (severity-med), 11+=rot (severity-high).
function severityClassByCount(count: number): string {
  if (count <= 0) return "count-zero";
  if (count <= 5) return "severity-low";
  if (count <= 10) return "severity-med";
  return "severity-high";
}

// =============================================================================
// Handlers
// =============================================================================

function attachShellHandlers(): void {
  attachSubsidebarHandlers();
  attachTopbarHandlers();
}

function attachSubsidebarHandlers(): void {
  document
    .querySelector<HTMLButtonElement>(".subsidebar-collapse-btn")
    ?.addEventListener("click", () => toggleSubsidebar());

  document
    .getElementById("subsidebar-expand-btn")
    ?.addEventListener("click", () => toggleSubsidebar());
}

function attachTopbarHandlers(): void {
  document.getElementById("header-logout-btn")?.addEventListener("click", () => void runLogout());
  // Topbar-Stempeluhr (Phase 8) — Live-Widget, mountet sich nach jedem Topbar-Render.
  const stempelSlot = document.getElementById("topbar-stempel-slot");
  if (stempelSlot) mountTopbarStempel(stempelSlot);
}

function toggleSubsidebar(): void {
  subsidebarCollapsed = !subsidebarCollapsed;
  const sub = document.getElementById("subsidebar");
  const expandBtn = document.getElementById("subsidebar-expand-btn");
  sub?.classList.toggle("is-collapsed", subsidebarCollapsed);
  expandBtn?.classList.toggle("is-visible", subsidebarCollapsed);
}

async function runLogout(): Promise<void> {
  const btn = document.getElementById("header-logout-btn") as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = true;
  }
  await logoutBackend();
  window.location.href = "/";
}

// =============================================================================
// Helpers
// =============================================================================

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

// =============================================================================
// Re-exports
// =============================================================================

export { findViewForRoute };
